import type { FC } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Flex,
  Space,
  Spin,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { GetKmsDocumentStatusResponse } from '~/app/services/kms/kms-document.types';
import { KmsDocumentService } from '~/app/services/kms/kms-document.service';
import { useKmsDocumentDetailFields } from './hooks/useKmsDocumentDetailFields';
import type { HitlCandidate } from '~/app/services/kms/kms-document.types';
import { KmsExtractedFieldsCard } from './components/KmsExtractedFieldsCard/KmsExtractedFieldsCard';
import { KmsAssetLinkCard } from './components/KmsAssetLinkCard/KmsAssetLinkCard';
import { KmsTypeCandidatesCard } from './components/KmsTypeCandidatesCard/KmsTypeCandidatesCard';

const { Title, Text } = Typography;

export interface KmsDocumentDetailPageViewProps {
  document: GetKmsDocumentStatusResponse | null;
  typeCandidates: HitlCandidate[];
  showTypeCandidates: boolean;
  isLoading: boolean;
  error: string | null;
  organizationId: string | null;
  isSuperAdmin: boolean;
  onBack: () => void;
}

export const KmsDocumentDetailPageView: FC<KmsDocumentDetailPageViewProps> = ({
  document,
  typeCandidates,
  showTypeCandidates,
  isLoading,
  error,
  organizationId,
  isSuperAdmin,
  onBack,
}) => {
  const { t } = useTranslation('kms');
  const { notification } = App.useApp();
  const fields = useKmsDocumentDetailFields(document);

  const openContent = async (inline: boolean) => {
    if (!document) {
      return;
    }
    try {
      await KmsDocumentService.openContent({
        documentId: document.documentId,
        organizationId,
        isSuperAdmin,
        inline,
      });
    } catch (err) {
      notification.error({
        message: t('detail.contentError'),
        description:
          err instanceof Error ? err.message : t('detail.contentError'),
      });
    }
  };

  return (
    <div
      className="app-page app-page--kms"
      style={{ width: '100%', minWidth: 0 }}
    >
      <Flex
        className="kms-page-header"
        justify="space-between"
        align="flex-start"
        gap={16}
        wrap="wrap"
      >
        <Space direction="vertical" size={4} style={{ minWidth: 0 }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            style={{ paddingInline: 0, height: 'auto' }}
          >
            {t('detail.back')}
          </Button>
          <Title level={2} className="ds-page-title" style={{ margin: 0 }}>
            {document?.fileName ?? t('detail.title')}
          </Title>
          <Text type="secondary" className="om-dashboard-header__subtitle">
            {t('detail.subtitle')}
          </Text>
        </Space>
        {document ? (
          <Space wrap>
            <Button
              type="primary"
              icon={<ReadOutlined />}
              onClick={() => void openContent(true)}
            >
              {t('detail.readDocument')}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => void openContent(false)}
            >
              {t('detail.downloadDocument')}
            </Button>
          </Space>
        ) : null}
      </Flex>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={t('detail.loadError')}
          description={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {isLoading ? (
        <Card className="kms-section-card">
          <Flex justify="center" style={{ padding: 48 }}>
            <Spin size="large" />
          </Flex>
        </Card>
      ) : document ? (
        <>
          <Card className="kms-section-card">
            <Descriptions
              column={{ xs: 1, sm: 1, md: 2 }}
              bordered
              size="middle"
              items={fields}
            />
          </Card>

          <KmsExtractedFieldsCard
            document={document}
            organizationId={organizationId}
            isSuperAdmin={isSuperAdmin}
          />

          {showTypeCandidates ? (
            <KmsTypeCandidatesCard candidates={typeCandidates} />
          ) : null}

          <KmsAssetLinkCard
            document={document}
            organizationId={organizationId}
            isSuperAdmin={isSuperAdmin}
          />
        </>
      ) : null}
    </div>
  );
};
